// components/HeatmapButtonCalendar.jsx
import PropTypes from "prop-types";
import {useState, useRef, useCallback, useEffect, forwardRef, cloneElement} from 'react';
import clsx from "clsx";
import moment from "moment";
import { isFunction } from "lodash";
import makeStyles from "@mui/styles/makeStyles";
import {Box, Button, Modal, Stack, Typography} from '@mui/material';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AgyleLoadingSpinner from "../other/agyleLoadingSpinner";
import {CalendarPicker, PickersDay} from '@mui/lab';
import AgyleButton from '../ui/buttons/agyleButton';
import {useTheme} from '@mui/material/styles';

export const HEAT_VALUE_COLOR = {
  HIGH_AVAILABILITY: "#4EAC6F",
  MEDIUM_AVAILABILITY: "#F7A202",
  LOW_AVAILABILITY: "#E25B61",
};

const useStyles = makeStyles((theme) => ({
  dateButton: {
    textTransform: "none",
    color: theme.palette.text.primary,

    "&:hover": {
      backgroundColor: `${theme.palette.background.paper}4F`,
    },
  },
  buttonHeatMarker: {
    height: "1.5rem",
    width: 5,
    borderRadius: 2,
    backgroundColor: "transparent",
  },
  icon: {
    transition: "transform 0.3s ease-in-out",
    display: "flex",
    alignItems: "center",
  },
  rotated: {
    transform: "rotate(180deg)",
    color: theme.palette.primary.main,
  },
  pickersDay: ({ selected, today }) => ({
    "&.MuiPickersDay-root": {
      backgroundColor: theme.palette.background.paperDarker,
      fontWeight: 500,
      fontSize: ".9rem",
        border: 'none',
        textDecorationLine: today && "underline",
        textUnderlineOffset: today &&"4px",
        textDecorationThickness: today &&"2px",

      "&.Mui-selected": {
        border: selected && "2px solid #FFF",
        backgroundColor: theme.palette.background.paperDarker,
      },
    },
  }),
  highAvailability: {
    backgroundColor: HEAT_VALUE_COLOR.HIGH_AVAILABILITY,
  },
  mediumAvailability: {
    backgroundColor: HEAT_VALUE_COLOR.MEDIUM_AVAILABILITY,
  },
  lowAvailability: {
    backgroundColor: HEAT_VALUE_COLOR.LOW_AVAILABILITY,
  },
  calendarPicker: {
    "& .MuiPickersCalendarHeader-weekDayLabel": {
      fontWeight: 600,
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
      textTransform: "uppercase",
      padding: theme.spacing(0.5),
    },
    "& .MuiButtonBase-root": {
      backgroundColor: theme.palette.background.paperDarker,
      fontWeight: 500,
      fontSize: ".9rem",
      border: "none",
      "&.MuiPickersDay-today": {
        textDecorationLine: "underline",
        textUnderlineOffset: "4px",
        textDecorationThickness: "2px",
      },
      "&.Mui-selected": {
        border: `2px solid ${theme.palette.mode === 'light' ? '#FEC864' : '#fff'}`,
        backgroundColor: theme.palette.background.paperDarker,
        color: theme.palette.designSystem.palette.text2,
      },
    },
  },
}));

function ServerDay({day, pickersDayProps, getDayStyle}) {
  return (
    <PickersDay
      {...pickersDayProps}
      className={clsx(isFunction(getDayStyle) && getDayStyle(day))}
    />
  );
}

// Ref as valid HTML el. ref, for positioning purposes
const HeatmapButtonCalendar = forwardRef(
  (
    {
      loading,
      children,
      initialDate,
      hideButton,
      button,
      heatmapData = {}, // { 'DD-MM-YYYY': heatValue }
      onDateChange = (date) => console.log(date),
      onMonthChange = (date) => console.log("DATE:", date),
      dayStyleCallback = (date, defaultStyle) => defaultStyle,
    },
    ref,
  ) => {
    const classes = useStyles();
    const themeStyle = useTheme();

    const [selectedDate, setSelectedDate] = useState(() =>
      initialDate ? moment(initialDate) : moment(),
    );
    const [isModalOpen, setModalOpen] = useState(false);
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);

    const handleButtonClick = useCallback(() => {
      const targetRef = !hideButton ? buttonRef : ref;
      if (targetRef && targetRef.current) {
        const rect = targetRef.current.getBoundingClientRect();
        const modalWidth = 355;
        const screenWidth = window.innerWidth;

        let left = rect.left + window.scrollX;

        if (left + modalWidth > screenWidth) {
          left = screenWidth - modalWidth - 10;
        }

        left = Math.max(10, left);
        setModalPosition({
          top: rect.bottom + window.scrollY,
          // left: rect.left + window.scrollX,
          left,
        });
      }
      setModalOpen(true);
    }, [hideButton, ref, buttonRef.current]);

    const handleCloseModal = useCallback(() => {
      setModalOpen(false);
      onDateChange(initialDate);
    }, [initialDate, onDateChange]);

    const getDayStyle = useCallback(
      (day) => {
        if (isFunction(dayStyleCallback)) {
          return dayStyleCallback(day);
        } else {
          const formattedDate = day.format("DD-MM-YYYY");
          const heatValue = heatmapData?.[formattedDate] || 0;

          const heatColor =
            heatValue > 5
              ? "#E25B61"
              : heatValue > 3
                ? "#F7A202"
                : heatValue > 0
                  ? "#4EAC6F"
                  : null;

          return {
            backgroundColor: heatColor,
            borderRadius: "50%",
            color: heatColor ? themeStyle.palette.designSystem.palette.text1 : "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          };
        }
      },
      [heatmapData, dayStyleCallback],
    );

    useEffect(() => {
      if (moment(initialDate).isValid()) {
        setSelectedDate(moment(initialDate));
      }
    }, [initialDate]);

    return (
      <Box>
        {!hideButton && (
          <Button
            disableRipple
            variant="text"
            ref={buttonRef}
            onClick={handleButtonClick}
            className={classes.dateButton}
            sx={{ p: 0.5 }}
          >
            <Stack direction="row" alignItems="center" gap={1} height="100%">
              <Box
                className={clsx(
                  classes.buttonHeatMarker,
                  isFunction(dayStyleCallback) &&
                    dayStyleCallback(selectedDate),
                )}
              />

              <Typography variant="h6">
                {selectedDate.format("DD MMM YYYY")}
              </Typography>

              <div
                className={clsx(classes.icon, isModalOpen && classes.rotated)}
              >
                <ArrowDropDownIcon />
              </div>
            </Stack>
          </Button>
        )}

        {hideButton && button ? (
            cloneElement(button, { onClick: handleButtonClick })
        ) : null}

        <Modal
          open={isModalOpen}
          onClose={handleCloseModal}
          disableAutoFocus
          BackdropProps={{ style: { opacity: 0 } }}
        >
          <Box
            sx={{
              position: "absolute",
              top: modalPosition.top,
              left: modalPosition.left,
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              p: 2,
            }}
          >
            {children}

            <CalendarPicker
                autoFocus
                views={['day']}
                loading={loading}
                value={selectedDate}
                date={selectedDate}
                displayStaticWrapperAs="desktop"
                className={clsx(classes.calendarPicker)}
                renderLoading={() => (
                    <AgyleLoadingSpinner disableText spinnerSize={60} />
                )}
                onChange={(date) => {
                  setSelectedDate(date);
                  onDateChange(date);
                }}
                onMonthChange={onMonthChange}
                renderDay={(day, selectedDates, pickersDayProps) => (
                    <ServerDay key={day} day={day} selectedDates={selectedDates} pickersDayProps={pickersDayProps} getDayStyle={getDayStyle} />)}
            />

            <Stack direction='row' justifyContent='flex-end'>
              <AgyleButton variant='outlined' colour='primary' onClick={handleCloseModal}>
                Close
              </AgyleButton>
            </Stack>
          </Box>
        </Modal>
      </Box>
    );
  },
);

export default HeatmapButtonCalendar;

HeatmapButtonCalendar.propTypes = {
  heatmapData: PropTypes.object,
  onDateChange: PropTypes.func,
  onMonthChange: PropTypes.func,
  children: PropTypes.object,
  dayStyleCallback: PropTypes.func,
  loading: PropTypes.bool,
  hideButton: PropTypes.bool,
};

HeatmapButtonCalendar.defaultProps = {
  hideButton: false,
};
